import { useState, Fragment, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import UserDropdown from '@/components/Dropdowns/UserDropdown';
import NotificationDropdown from '@/components/Dropdowns/NotificationDropdown';
import useAuth from '@/hooks/useAuth';
import { UserRole, hasAnyUserRole } from '@/lib/userRoles';

export default function Sidebar({ open, setOpen }: { open: boolean, setOpen: any; }) {
  const { user } = useAuth();

  /* used to control open menu on mobile */
  const [collapseShow, setCollapseShow] = useState('hidden');
  const navbarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {

    const handleClickOutside = (event: MouseEvent) => {
      if (navbarRef.current && !(navbarRef.current as any).contains(event.target as Node)) {
        setCollapseShow('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };

  }, []);

  const menuSections = [
    {
      sectionTitle: 'Admin',
      principalType: 'user',
      allowedUserRoles: [UserRole.MASTER, UserRole.PROGRAMMER, UserRole.SELLER, UserRole.PARTNER],
      items: [
        // {
        //   title: 'Dashboard',
        //   to: '/admin/dashboard', icon: 'fas fa-tv',
        // },
        {
          title: 'Crm',
          to: '/admin/crm', icon: 'fas fa-tv',
          principalType: 'user',
          allowedUserRoles: [UserRole.MASTER, UserRole.PROGRAMMER],
        },
        {
          title: 'Companies',
          to: '/admin/companies', icon: 'fas fa-tv',
          principalType: 'user',
          allowedUserRoles: [UserRole.MASTER, UserRole.PROGRAMMER, UserRole.PARTNER],
        },
        {
          title: 'Partners',
          to: '/admin/partners', icon: 'fas fa-handshake',
          principalType: 'user',
          allowedUserRoles: [UserRole.MASTER, UserRole.PROGRAMMER],
        },
        {
          title: 'Affiliates',
          to: '/admin/affiliates', icon: 'fas fa-user-friends',
          principalType: 'user',
          allowedUserRoles: [UserRole.MASTER],
        },
      ]
    },
    // {
    //   sectionTitle: 'Volver',
    //   principalType: 'Company',
    //   items: [
    //     {
    //       title: 'Volver al portal',
    //       to: '/shop/eod',
    //       icon: 'far fa-calendar-days',
    //       principalType: 'Company',
    //     },
    //   ]
    // },
    {
      sectionTitle: 'Reportes',
      principalType: 'Company',
      items: [
        {
          title: 'Dashboard',
          to: '/shop/dashboard', icon: 'fas fa-tv',
        },
        {
          title: 'Cierre del dia',
          to: '/shop/reports/eod', icon: 'far fa-calendar-days',
          principalType: 'Company',
        },
        {
          title: 'Ticket Promedio',
          to: '/shop/reports/averageTicket', icon: 'far fa-dollar',
          principalType: 'Company',
        },
        {
          title: 'Productividad por tecnico',
          to: '/shop/reports/productivity', icon: 'fa-solid fa-user-group',
          principalType: 'Company',
        },

        //
        {
          title: 'Ventas por Asesor',
          to: '/shop/reports/service_writer', icon: 'fa-solid fa-store',
          principalType: 'Company',
        },
        {
          title: 'Satisfacción del Cliente',
          to: '/shop/reports/surveys', icon: 'fa-solid fa-store',
          principalType: 'Company',
        },
        {
          title: 'Canal de cliente',
          to: '/shop/reports/customer_type', icon: 'fa-solid fa-user',
          principalType: 'Company',
        },
        {
          title: 'Por Marca de Vehículo',
          to: '/shop/reports/vehicles', icon: 'fa-solid fa-car',
          principalType: 'Company',
        },
        //
        {
          title: 'Reporte de Citas',
          to: '/shop/reports/appoiments', icon: 'fa-regular fa-calendar',
          principalType: 'Company',
        },
        {
          title: 'Ciclo de servicio',
          to: '/shop/reports/cycleTime', icon: 'fa-solid fa-clock',
          principalType: 'Company',
        },
        {
          title: 'Usabilidad',
          to: '/shop/reports/usability', icon: 'fas fa-rocket',
          principalType: 'Company',
        },
        // {
        //   title: 'Clientes',
        //   to: '/shop/reports/customers', icon: 'fas fa-users',
        //   principalType: 'Company',
        // },
        {
          title: 'Vehiculos entregados',
          to: '/shop/reports/delivered', icon: 'fas fa-car',
          principalType: 'Company'
        },
        // {
        //   title: 'Órdenes y Cotizaciones',
        //   to: '/shop/services', icon: 'fas fa-list',
        //   principalType: 'Company',
        // },
        {
          title: 'Reprocesos y garantia',
          to: '/shop/reports/warranty', icon: 'fas fa-list',
          principalType: 'Company'
        },
        // {
        //   title: 'CRM',
        //   to: '/shop/crm', icon: 'fas fa-rocket',
        //   principalType: 'Company',
        // },
        // {
        //   title: 'Inventario',
        //   to: '/shop/reports/inventory', icon: 'fas fa-users',
        //   principalType: 'Company',
        // },
        // { title: 'Settings', to: '/admin/settings', icon: 'fas fa-tools' },
        // { title: 'Tables', to: '/admin/tables', icon: 'fas fa-table' },
        // { title: 'Maps', to: '/admin/maps', icon: 'fas fa-map-marked' },
      ]
    },
    {
      sectionTitle: 'Otras',
      principalType: ['Company'],
      items: [
        { title: 'Perfil', to: '/shop/profile', icon: 'fas fa-user-circle' },
      ]
    },
    {
      sectionTitle: 'Otras',
      principalType: ['user'],
      allowedUserRoles: [UserRole.MASTER, UserRole.PROGRAMMER, UserRole.SELLER, UserRole.PARTNER],
      items: [
        { title: 'Perfil', to: '/admin/profile', icon: 'fas fa-user-circle' },
      ]
    },
  ];

  // Función para verificar si el usuario puede ver una sección
  const canViewSection = (section: any) => {
    // Si es un usuario (principalType: 'user' o incluye 'user')
    if (user.principalType === 'user') {
      // Si la sección especifica roles permitidos para usuarios, verificar
      if (section.allowedUserRoles) {
        return hasAnyUserRole(user.user.role, section.allowedUserRoles);
      }
    }
    
    // Lógica original para principalType
    if (section.principalType) {
      return Array.isArray(section.principalType) 
        ? section.principalType.includes(user.principalType)
        : section.principalType === user.principalType;
    }
    
    return true;
  };

  // Función para verificar si el usuario puede ver un item específico
  const canViewItem = (item: any) => {
    // Si es un usuario (principalType: 'user')
    if (user.principalType === 'user') {
      // Si el item especifica roles permitidos para usuarios, verificar
      if (item.allowedUserRoles) {
        return hasAnyUserRole(user.user.role, item.allowedUserRoles);
      }
    }
    
    // Lógica original para principalType del item
    if (item.principalType) {
      return Array.isArray(item.principalType) 
        ? item.principalType.includes(user.principalType)
        : item.principalType === user.principalType;
    }
    
    return true;
  };

  return (
    <>
      <nav
        ref={navbarRef}
        className={
          `md:left-0 md:block md:fixed md:top-0 md:bottom-0 md:overflow-y-auto md:flex-row md:flex-nowrap md:overflow-hidden shadow-xl bg-white flex flex-wrap items-center justify-between relative z-10 py-4 px-6 duration-300
        + ${open ? 'md:w-64' : 'md:w-20'}`}>
        <div className='md:flex-col md:items-stretch md:min-h-full md:flex-nowrap px-0 flex flex-wrap items-center justify-between w-full mx-auto'>
          {/* Toggler */}
          <button
            className='cursor-pointer text-black opacity-50 md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent'
            type='button'
            onClick={() => setCollapseShow('bg-white m-2 py-3 px-6')}
          >
            <i className='fas fa-bars'></i>
          </button>
          {/* Brand */}
          <span className='hidden md:flex justify-between p-4 px-0 duration-300'>
            {open && <Link
              className='md:pb-2 text-slate-600 mr-0 whitespace-nowrap text-sm uppercase font-bold'
              to='/admin/dashboard'
            >
              Mecanica MX
            </Link>}

            <span onClick={() => setOpen(!open)}>
              <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor' className='w-6 h-6'>
                <path strokeLinecap='round' strokeLinejoin='round' d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5' />
              </svg>

            </span>

          </span>
          {/* User */}
          <ul className='md:hidden items-center flex flex-wrap list-none'>
            <li className='inline-block relative'>
              <NotificationDropdown />
            </li>
            <li className='inline-block relative'>
              <UserDropdown />
            </li>
          </ul>
          {/* Collapse */}
          <div
            className={
              'md:flex md:flex-col md:items-stretch md:opacity-100 md:relative md:mt-4 md:shadow-none shadow absolute top-0 left-0 right-0 z-40 overflow-y-auto overflow-x-hidden h-auto items-center flex-1 rounded ' +
              collapseShow
            }
          >
            {/* Collapse header */}
            <div className='md:min-w-full md:hidden block pb-4 mb-4 border-b border-solid border-slate-200'>
              <div className='flex flex-wrap'>
                <div className='w-6/12'>
                  <Link
                    className='md:block text-left md:pb-2 text-slate-600 mr-0 inline-block whitespace-nowrap text-sm uppercase font-bold p-4 px-0'
                    to='/'
                  >
                    Mecanica MX
                  </Link>
                </div>
                <div className='w-6/12 flex justify-end'>
                  <button
                    type='button'
                    className='cursor-pointer text-black opacity-50 md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent'
                    onClick={() => setCollapseShow('hidden')}
                  >
                    <i className='fas fa-times'></i>
                  </button>
                </div>
              </div>
            </div>
            {/* Form */}
            {/* <form className='mt-6 mb-4 md:hidden'>
              <div className='mb-3 pt-0'>
                <input
                  type='text'
                  placeholder='Search'
                  className='border-0 px-3 py-2 h-12 border-solid  border-slate-500 placeholder-slate-300 text-slate-600 bg-white rounded text-base leading-snug shadow-none outline-none focus:outline-none w-full font-normal'
                />
              </div>
            </form> */}


            {
              menuSections
                .filter(canViewSection)
                .map((ms, i) => (
                  <Fragment key={i}>
                    <hr className='my-4 md:min-w-full' />
                    {open && <h6 className='md:min-w-full text-slate-500 text-xs uppercase font-bold block pt-1 pb-4 no-underline duration-300'>
                      {ms.sectionTitle}
                    </h6>}
                    <ul className='md:flex-col md:min-w-full flex flex-col list-none'>
                      {ms.items
                        .filter(canViewItem)
                        .map((item, i) => (
                          <li className='items-center' key={i}>
                            <NavLink
                              className={({ isActive }) =>
                                'text-xs uppercase py-3 font-bold block ' +
                                (isActive
                                  ? 'text-sky-500 hover:text-sky-600'
                                  : 'text-slate-700 hover:text-slate-500')
                              }
                              to={item.to}
                              onClick={() => setCollapseShow('hidden')}
                            >
                              {({ isActive }) =>
                                <>
                                  <i
                                    className={
                                      `${item.icon || ''} ${open ? 'text-sm mr-2' : 'text-xl'} duration-300` +
                                      (isActive ? 'opacity-75' : 'text-slate-300')
                                    }
                                  ></i>
                                  {open && item.title}
                                </>
                              }

                            </NavLink>
                          </li>
                        ))}

                    </ul>
                  </Fragment>
                ))
            }

          </div>
        </div>
      </nav>
    </>
  );
}
